// Each battery bank's chemistry, one row per bank the boat reports (TOM-318,
// core ADR-197).
//
// NO OPTIMISTIC UI, as in `VesselTypeChoice`. The pressed button is the boat's
// own snapshot (`override`), never the click. After a send the row says "Sent
// to your boat…" and the component re-reads the snapshot every few seconds
// until the boat confirms or the deadline passes. The in-flight claim is owned
// here, per bank, and ended by the echo or the deadline.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { BOAT_ECHO_TIMEOUT_MS } from '../../lib/boatEcho';
import {
  BATTERY_CHEMISTRY_OFFLINE_REASON,
  BATTERY_CHEMISTRY_OPTIONS,
  batteryBankLabel,
  batteryChemistryStatusText,
  batteryChemistryUsedText,
  fetchBatteryChemistry,
  sendBatteryChemistry,
  settleBatteryChemistry,
  timeOutBatteryChemistry,
  type BatteryChemistryMirror,
  type BatteryChemistryOptionValue,
  type BatteryChemistrySaveState,
} from '../../lib/batteryChemistry';

/** How often the page re-reads the snapshot while a send is in flight. */
const ECHO_POLL_MS = 3_000;

interface BatteryChemistryChoiceProps {
  boatId: string;
  boatOnline: boolean;
}

type Load =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly mirror: BatteryChemistryMirror }
  | { readonly kind: 'error' };

export function BatteryChemistryChoice({ boatId, boatOnline }: BatteryChemistryChoiceProps) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [saves, setSaves] = useState<Readonly<Record<string, BatteryChemistrySaveState>>>({});

  const refresh = useCallback(async () => {
    try {
      setLoad({ kind: 'ready', mirror: await fetchBatteryChemistry(boatId) });
    } catch {
      setLoad((prev) => (prev.kind === 'ready' ? prev : { kind: 'error' }));
    }
  }, [boatId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The boat's snapshot ends each bank's in-flight request.
  useEffect(() => {
    if (load.kind !== 'ready') return;
    const banks = load.mirror.snapshot?.banks ?? [];
    setSaves((prev) => {
      let changed = false;
      const next: Record<string, BatteryChemistrySaveState> = { ...prev };
      for (const [bank, save] of Object.entries(prev)) {
        const mirrored = banks.find((b) => b.bank === bank)?.override;
        const settled = settleBatteryChemistry(save, mirrored);
        if (settled !== save) {
          next[bank] = settled;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [load]);

  // While any bank waits for the boat, re-read the snapshot.
  const waiting = Object.values(saves).some((s) => s.kind === 'sent');
  useEffect(() => {
    if (!waiting) return;
    const id = window.setInterval(() => void refresh(), ECHO_POLL_MS);
    return () => window.clearInterval(id);
  }, [waiting, refresh]);

  // …and a deadline ends the wait when no echo comes.
  useEffect(() => {
    if (!waiting) return;
    const id = window.setTimeout(() => {
      setSaves((prev) => {
        const next: Record<string, BatteryChemistrySaveState> = {};
        for (const [bank, save] of Object.entries(prev)) next[bank] = timeOutBatteryChemistry(save);
        return next;
      });
    }, BOAT_ECHO_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, [waiting]);

  async function choose(bank: string, value: BatteryChemistryOptionValue): Promise<void> {
    const current = saves[bank];
    if (current?.kind === 'sending') return;
    setSaves((prev) => ({ ...prev, [bank]: { kind: 'sending', value } }));
    const outcome = await sendBatteryChemistry(boatId, bank, value);
    setSaves((prev) => ({ ...prev, [bank]: outcome }));
    if (outcome.kind === 'sent') void refresh();
  }

  if (load.kind === 'loading') {
    return <p className="muted">Loading your boat’s batteries…</p>;
  }
  if (load.kind === 'error') {
    return (
      <p className="muted" data-testid="battery-chemistry-error">
        Couldn’t load your boat’s batteries — reload the page to try again.
      </p>
    );
  }
  const snapshot = load.mirror.snapshot;
  if (snapshot === null) {
    return (
      <p className="muted" data-testid="battery-chemistry-none">
        Your boat has not reported its batteries yet. This needs Neomaris Core 0.10.3 or newer.
      </p>
    );
  }
  if (snapshot.banks.length === 0) {
    return (
      <p className="muted" data-testid="battery-chemistry-none">
        Your boat does not report any battery voltage.
      </p>
    );
  }

  return (
    <div className="battery-chemistry" data-testid="battery-chemistry">
      {snapshot.banks.map((bank) => {
        const save = saves[bank.bank] ?? { kind: 'idle' };
        const busy = save.kind === 'sending';
        const status = batteryChemistryStatusText(save);
        const warn =
          save.kind === 'error' || save.kind === 'offline' || save.kind === 'unconfirmed';
        return (
          <div
            key={bank.bank}
            className="battery-chemistry__bank"
            data-testid={`battery-chemistry-bank-${bank.bank}`}
          >
            <h4 className="battery-chemistry__name">{batteryBankLabel(bank)}</h4>
            <div className="vessel-type__options" role="group" aria-label={batteryBankLabel(bank)}>
              {BATTERY_CHEMISTRY_OPTIONS.map((option) => {
                const selected = bank.override === option.value;
                return (
                  <button
                    key={option.key}
                    type="button"
                    className={`vessel-type__option${selected ? ' vessel-type__option--on' : ''}`}
                    aria-pressed={selected}
                    disabled={busy || !boatOnline}
                    data-testid={`battery-chemistry-${bank.bank}-${option.key}`}
                    onClick={() => void choose(bank.bank, option.value)}
                  >
                    <span className="vessel-type__label">{option.label}</span>
                  </button>
                );
              })}
            </div>
            <p className="muted" data-testid={`battery-chemistry-${bank.bank}-used`}>
              {batteryChemistryUsedText(bank)}
            </p>
            {status !== null ? (
              <p
                className={`vessel-type__status${warn ? ' vessel-type__status--warn' : ''}`}
                role="status"
                aria-live="polite"
                data-testid={`battery-chemistry-${bank.bank}-status`}
              >
                {status}
              </p>
            ) : null}
          </div>
        );
      })}
      {!boatOnline ? (
        <p className="vessel-type__offline" data-testid="battery-chemistry-offline-reason">
          {BATTERY_CHEMISTRY_OFFLINE_REASON}
        </p>
      ) : null}
      <p className="muted" data-testid="battery-chemistry-alarms-note">
        Changing a battery’s chemistry does not change alarms that are already set.{' '}
        <Link to={`/dashboard/boats/${boatId}/alarms/regenerate`}>Review your alarms</Link> to apply
        the new thresholds. A battery set to lead-acid keeps any state-of-charge alarm it already
        has until you delete it.
      </p>
    </div>
  );
}
